import React, { useState, useEffect } from 'react';
import { SecurityPolicy } from '../types';
import { policiesAPI } from '../services/api';
import Toggle from '../components/Toggle';
import '../styles/PoliciesPage.css';

const PoliciesPage: React.FC = () => {
  const [policies, setPolicies] = useState<SecurityPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    name: string;
    namespace: string;
    type: string;
    description: string;
  }>({
    name: '',
    namespace: 'default',
    type: 'file',
    description: '',
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    fetchPolicies();
  }, []);

  const fetchPolicies = async () => {
    try {
      setLoading(true);
      const data = await policiesAPI.getAll();
      setPolicies(data);
      setError(null);
    } catch (err) {
      setError('Failed to load policies');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    const errors: Record<string, string> = {};
    
    if (!formData.name.trim()) {
      errors.name = 'Policy name is required';
    }
    if (formData.name.length < 3) {
      errors.name = 'Policy name must be at least 3 characters';
    }
    if (formData.name.length > 100) {
      errors.name = 'Policy name must not exceed 100 characters';
    }
    
    if (!formData.namespace.trim()) {
      errors.namespace = 'Namespace is required';
    }
    
    if (!formData.type) {
      errors.type = 'Policy type is required';
    }
    
    if (formData.description.length > 500) {
      errors.description = 'Description must not exceed 500 characters';
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      if (editingId) {
        await policiesAPI.update(editingId, {
          ...formData,
          enabled: true,
        });
      } else {
        await policiesAPI.create({
          ...formData,
          enabled: true,
        });
      }
      await fetchPolicies();
      resetForm();
      setError(null);
    } catch (err) {
      setError('Failed to save policy');
      console.error(err);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      namespace: 'default',
      type: 'file',
      description: '',
    });
    setFormErrors({});
    setShowForm(false);
    setEditingId(null);
  };

  const handleEdit = (policy: SecurityPolicy) => {
    setFormData({
      name: policy.name,
      namespace: policy.namespace || 'default',
      type: policy.type || 'file',
      description: policy.description,
    });
    setEditingId(policy.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this policy?')) {
      try {
        await policiesAPI.delete(id);
        await fetchPolicies();
        setError(null);
      } catch (err) {
        setError('Failed to delete policy');
        console.error(err);
      }
    }
  };

  const handleToggleEnabled = async (id: string, currentEnabled: boolean) => {
    try {
      await policiesAPI.toggleEnabled(id, !currentEnabled);
      await fetchPolicies();
      setError(null);
    } catch (err) {
      setError('Failed to update policy status');
      console.error(err);
    }
  };

  const uniqueNamespaces = Array.from(new Set(policies.map((p) => p.namespace)));

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="policies-page">
        <header className="page-header">
          <h1>Security Policies</h1>
          <p>Manage KubeArmor security policies</p>
        </header>
        <div className="loading">Loading policies...</div>
      </div>
    );
  }

  return (
    <div className="policies-page">
      <header className="page-header">
        <h1>Security Policies</h1>
        <p>Total policies: {policies.length}</p>
      </header>

      <div className="page-container">
        {error && <div className="error">{error}</div>}

        <section className="action-bar">
          <button
            className="btn btn-primary"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            + New Policy
          </button>
        </section>

        {showForm && (
          <section className="form-section">
            <h2>{editingId ? 'Edit Policy' : 'Create New Policy'}</h2>
            <form onSubmit={handleSubmit} className="policy-form">
              <div className="form-group">
                <label>Policy Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => {
                    setFormData({ ...formData, name: e.target.value });
                    setFormErrors({ ...formErrors, name: '' });
                  }}
                  placeholder="e.g., Restrict System Calls"
                  className={formErrors.name ? 'error' : ''}
                  required
                />
                {formErrors.name && <span className="error-message">{formErrors.name}</span>}
              </div>

              <div className="form-group">
                <label>Namespace *</label>
                <select
                  value={formData.namespace}
                  onChange={(e) => {
                    setFormData({ ...formData, namespace: e.target.value });
                    setFormErrors({ ...formErrors, namespace: '' });
                  }}
                  className={formErrors.namespace ? 'error' : ''}
                >
                  {uniqueNamespaces.map((ns) => (
                    <option key={ns} value={ns}>
                      {ns}
                    </option>
                  ))}
                  <option value="default">default</option>
                </select>
                {formErrors.namespace && <span className="error-message">{formErrors.namespace}</span>}
              </div>

              <div className="form-group">
                <label>Policy Type *</label>
                <select
                  value={formData.type}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      type: e.target.value as 'file' | 'process' | 'network' | 'capability',
                    })
                  }
                  className={formErrors.type ? 'error' : ''}
                >
                  <option value="file">File Access</option>
                  <option value="process">Process Execution</option>
                  <option value="network">Network Connection</option>
                  <option value="capability">Capability Usage</option>
                </select>
                {formErrors.type && <span className="error-message">{formErrors.type}</span>}
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => {
                    setFormData({ ...formData, description: e.target.value });
                    setFormErrors({ ...formErrors, description: '' });
                  }}
                  placeholder="Policy description..."
                  rows={4}
                  className={formErrors.description ? 'error' : ''}
                />
                {formErrors.description && <span className="error-message">{formErrors.description}</span>}
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                >
                  Cancel
                </button>
              </div>
            </form>
          </section>
        )}

        <section className="policies-table-section">
          <table className="policies-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Namespace</th>
                <th>Type</th>
                <th>Status</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {policies.map((policy) => (
                <tr key={policy.id}>
                  <td className="policy-name">{policy.name}</td>
                  <td>{policy.namespace}</td>
                  <td>
                    <span className={`type-badge type-${policy.type}`}>
                      {policy.type}
                    </span>
                  </td>
                  <td>
                    <Toggle
                      checked={policy.enabled}
                      onChange={() => handleToggleEnabled(policy.id, policy.enabled)}
                    />
                  </td>
                  <td>{formatDate(policy.createdAt)}</td>
                  <td className="actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEdit(policy)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(policy.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {policies.length === 0 && (
            <div className="no-data">No policies found. Create your first policy!</div>
          )}
        </section>
      </div>
    </div>
  );
};

export default PoliciesPage;
